import { CatalogTranscription, OrderTranscription } from 'config/constants/transcription.constant';
import UserService from 'config/services/user.service';
import { CartResponse } from 'config/types/user.type';
import { isAuthError } from 'config/utils/errors.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import { store } from '../../../store';
import { CartItemAction, CartItemProduct, Item } from '../../../store/models/cart.model';
import { Product, ProductImage, ProductVariant } from '../../../store/models/catalog.model';
import { CartSlice } from '../../../store/slices/cart.slice';
import { OrderSlice } from '../../../store/slices/order.slice';

export type CartItemLike = Pick<Item, 'product' | 'variant' | 'quantity'>;

export const normalizeCartProductId = (id: unknown): string => String(id ?? '');

export const normalizeCartOption = (option?: string): string => String(option ?? '').trim();

export const getCartItemKey = (item: Pick<CartItemLike, 'product' | 'variant' | 'quantity'>): string => {
  const sku = item.variant?.sku?.trim();
  if (sku) return sku;
  return `${normalizeCartProductId(item.product._id)}:${normalizeCartOption(item.variant?.option)}`;
};

export const isSameCartItem = (
  item: Pick<CartItemLike, 'product' | 'variant' | 'quantity'>,
  productId: string,
  variant: Pick<Item['variant'], 'sku' | 'option'>,
): boolean =>
  getCartItemKey(item) ===
  getCartItemKey({
    product: { _id: productId, name: '', slug: '' },
    variant,
    quantity: 0,
  });

export const buildBaselineFromItems = (items: CartItemLike[]): Record<string, number> => {
  const baseline: Record<string, number> = {};
  items.forEach((item) => {
    const key = getCartItemKey(item);
    baseline[key] = (baseline[key] ?? 0) + item.quantity;
  });
  return baseline;
};

export const computeMergeDelta = (localItems: CartItemLike[], syncedBaseline: Record<string, number>): CartItemLike[] => {
  const localMap = new Map<string, CartItemLike>();

  localItems.forEach((item) => {
    const key = getCartItemKey(item);
    const existing = localMap.get(key);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      localMap.set(key, {
        ...item,
        product: { ...item.product },
        variant: { ...item.variant },
      });
    }
  });

  const delta: CartItemLike[] = [];
  localMap.forEach((item, key) => {
    const baselineQty = syncedBaseline[key] ?? 0;
    const deltaQty = item.quantity - baselineQty;
    if (deltaQty > 0) {
      delta.push({ ...item, quantity: deltaQty });
    }
  });

  return delta;
};

export const capQuantityToStock = <T extends CartItemLike>(item: T): T => {
  const stock = Number(item.variant.stock);
  if (!Number.isFinite(stock) || stock <= 0) return item;
  if (item.quantity <= stock) return item;
  return { ...item, quantity: stock };
};

export const computeSomeItemOutOfStock = (items: CartItemLike[]): boolean =>
  items.some((item) => {
    const stock = Number(item.variant.stock);
    if (!Number.isFinite(stock)) return false;
    return item.quantity > stock || stock <= 0;
  });

const CART_SYNC_DEBOUNCE_MS = 400; // TODO: esto hay que moverlo a constants

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let syncChain: Promise<void> = Promise.resolve();
let syncGeneration = 0;
let hasHydratedCart = false;
let visibilityListenerAttached = false;

const createCartOpId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isAuthenticated = (): boolean => {
  const { accessToken, isAuthenticated: authenticated } = store.getState().auth;
  return Boolean(authenticated && accessToken);
};

const hasSku = (sku?: string): boolean => Boolean(sku && String(sku).trim());

const sanitizeItemsForSync = (items: Item[]): Item[] =>
  items
    .filter(
      (item) =>
        Boolean(item.product?._id) &&
        (hasSku(item.variant?.sku) || Boolean(normalizeCartOption(item.variant?.option))) &&
        item.quantity > 0,
    )
    .map(capQuantityToStock);

const shouldRejectEmptyServerCart = (sentCount: number, response: CartResponse): boolean =>
  sentCount > 0 && (response.items ?? []).length === 0;

const notifyCartAdjustments = (response: CartResponse): void => {
  if ((response.removedItems?.length ?? 0) > 0) {
    SnackbarUtilities.error(OrderTranscription.ERROR_SYNC_CART);
  }
  if ((response.quantityAdjustedItems?.length ?? 0) > 0) {
    SnackbarUtilities.error(CatalogTranscription.SOME_ITEMS_OUT_OF_STOCK);
  }
};

const toCartApiImage = (image: ProductImage): ProductImage | null => {
  const desktopImage = image.desktopImage || image.mobileImage;
  const mobileImage = image.mobileImage || image.desktopImage;
  if (!desktopImage || !mobileImage) return null;
  return {
    desktopImage,
    mobileImage,
    isMain: Boolean(image.isMain),
  };
};

export function toCartApiItems(items: Item[]): Item[] {
  return sanitizeItemsForSync(items).map((item) => ({
    product: {
      _id: String(item.product._id),
      name: item.product.name,
      slug: item.product.slug,
    },
    quantity: item.quantity,
    variant: {
      option: item.variant.option,
      stock: item.variant.stock,
      price: item.variant.price,
      priceFormatted: item.variant.priceFormatted,
      sku: item.variant.sku || undefined,
      images: (item.variant.images ?? []).map(toCartApiImage).filter((image): image is ProductImage => Boolean(image)),
    },
  }));
}

const keepCardPrice = (item: Item, previousItems: Item[]): Item => {
  if (item.variant.priceFormatted) return item;
  const previous = previousItems.find((prev) => isSameCartItem(prev, item.product._id, item.variant));
  if (!previous?.variant.priceFormatted) return item;
  return {
    ...item,
    variant: {
      ...item.variant,
      priceFormatted: previous.variant.priceFormatted,
    },
  };
};

const applyCartData = (items: Item[], cartId: string | null, someItemOutOfStock?: boolean): void => {
  const previousItems = store.getState().cart.items;
  const normalizedItems = items.map(capQuantityToStock).map((item) => keepCardPrice(item, previousItems));
  store.dispatch(
    CartSlice.refreshCartData({
      items: normalizedItems,
      cartId: cartId || null,
      someItemOutOfStock: someItemOutOfStock ?? computeSomeItemOutOfStock(normalizedItems),
    }),
  );
};

const applyServerCart = (response: Pick<CartResponse, 'items' | 'cartId' | 'someItemOutOfStock'>): void => {
  applyCartData(response.items ?? [], response.cartId ?? null, response.someItemOutOfStock);
  store.dispatch(CartSlice.refreshSyncedBaseline(buildBaselineFromItems(response.items ?? [])));
  notifyCartAdjustments(response as CartResponse);
};

const applyLocalItems = (items: Item[]): void => {
  const { cartId } = store.getState().cart;
  applyCartData(items, cartId);
};

const clearDebounce = (): void => {
  if (!debounceTimer) return;
  clearTimeout(debounceTimer);
  debounceTimer = null;
};

const isEpochStale = (generation: number): boolean => generation !== syncGeneration;

const bumpEpoch = (): number => {
  syncGeneration += 1;
  return syncGeneration;
};

const enqueueCartOperation = <T>(operation: () => Promise<T>): Promise<T> => {
  const next = syncChain.then(operation);
  syncChain = next.then(() => undefined).catch(() => undefined);
  return next;
};

const runSync = async (items: Item[], cartOpId: string): Promise<CartResponse | null> => {
  const generation = bumpEpoch();
  try {
    const payloadItems = toCartApiItems(items);
    const response = await UserService.syncCart(payloadItems, { cartOpId });
    if (isEpochStale(generation)) return null;
    if (shouldRejectEmptyServerCart(payloadItems.length, response)) {
      SnackbarUtilities.error(OrderTranscription.ERROR_SYNC_CART);
      return null;
    }
    applyServerCart(response);
    return response;
  } catch (error) {
    if (isEpochStale(generation)) return null;
    if (!isAuthError(error)) SnackbarUtilities.error(OrderTranscription.ERROR_SYNC_CART);
    return null;
  }
};

const flushPendingSync = (): Promise<CartResponse | null> => {
  clearDebounce();
  if (!isAuthenticated()) return Promise.resolve(null);
  const cartOpId = createCartOpId();
  return enqueueCartOperation(() => runSync(store.getState().cart.items, cartOpId));
};

const scheduleSync = (): void => {
  if (!isAuthenticated()) return;
  clearDebounce();
  debounceTimer = setTimeout(() => {
    flushPendingSync();
  }, CART_SYNC_DEBOUNCE_MS);
};

const increaseOrDecreaseItemInCart = (product: { _id: string }, action: CartItemAction, selectedVariant: ProductVariant): void => {
  const { items } = store.getState().cart;
  const delta = action === CartItemAction.ADD_ITEM ? 1 : -1;
  const nextItems = items.map((item) => {
    if (!isSameCartItem(item, product._id, selectedVariant)) return item;
    const nextQuantity = item.quantity + delta;
    if (nextQuantity < 1) return item;
    return capQuantityToStock({ ...item, quantity: nextQuantity });
  });

  applyLocalItems(nextItems);
  scheduleSync();
};

const addToCart = (product: Pick<Product, '_id' | 'name' | 'slug'>, selectedVariant: ProductVariant): void => {
  const { items } = store.getState().cart;
  const existingItem = items.find((item) => isSameCartItem(item, product._id, selectedVariant));

  if (existingItem) {
    increaseOrDecreaseItemInCart(product, CartItemAction.ADD_ITEM, selectedVariant);
    return;
  }

  const cartProduct: CartItemProduct = {
    _id: product._id,
    name: product.name,
    slug: product.slug,
  };
  const nextItems: Item[] = [...items, { product: cartProduct, quantity: 1, variant: selectedVariant }];
  applyLocalItems(nextItems);
  scheduleSync();
};

const removeToCart = (product: { _id: string }, selectedVariant: ProductVariant): void => {
  const { items } = store.getState().cart;
  const nextItems = items.filter((item) => !isSameCartItem(item, product._id, selectedVariant));
  applyLocalItems(nextItems);
  scheduleSync();
};

const discardCart = async (): Promise<void> => {
  clearDebounce();
  const generation = bumpEpoch();
  applyCartData([], null, false);
  store.dispatch(CartSlice.refreshSyncedBaseline({}));
  store.dispatch(OrderSlice.refreshEntryCode(null));

  if (!isAuthenticated()) return;

  await enqueueCartOperation(async () => {
    store.dispatch(CartSlice.fetchingRefresh(true));
    try {
      const cartOpId = createCartOpId();
      const response = await UserService.syncCart([], { cartOpId });
      if (isEpochStale(generation)) return;
      applyServerCart(response);
    } catch (error) {
      if (!isEpochStale(generation) && !isAuthError(error)) {
        SnackbarUtilities.error(OrderTranscription.ERROR_DISCARD_CART);
      }
    } finally {
      if (!isEpochStale(generation)) {
        store.dispatch(CartSlice.fetchingRefresh(false));
      }
    }
  });
};

const clearCartAfterPurchase = (): Promise<void> => discardCart();

const reconcileCartAfterAuth = async (): Promise<void> => {
  const generation = syncGeneration;
  await enqueueCartOperation(async () => {
    const { items, syncedBaseline } = store.getState().cart;
    store.dispatch(CartSlice.fetchingRefresh(true));
    try {
      const deltaItems = computeMergeDelta(items, syncedBaseline ?? {});
      const cartOpId = createCartOpId();
      const mergeOpId = createCartOpId();

      const response =
        deltaItems.length > 0 ?
          await UserService.mergeCart(toCartApiItems(deltaItems as Item[]), { cartOpId, mergeOpId })
        : await UserService.checkUserCart({ cartOpId });

      if (isEpochStale(generation)) return;

      if (deltaItems.length > 0 && shouldRejectEmptyServerCart(deltaItems.length, response)) {
        SnackbarUtilities.error(OrderTranscription.ERROR_MERGE_CART);
        return;
      }

      applyServerCart(response);
    } catch (_error) {
      if (!isEpochStale(generation)) {
        const { items: localItems } = store.getState().cart;
        SnackbarUtilities.error(localItems.length > 0 ? OrderTranscription.ERROR_MERGE_CART : OrderTranscription.ERROR_CHECK_USER_CART);
      }
    } finally {
      if (!isEpochStale(generation)) {
        store.dispatch(CartSlice.fetchingRefresh(false));
      }
    }
  });
};

type HydrateCartOptions = {
  skipPush?: boolean;
};

const hydrateCart = async (options?: HydrateCartOptions): Promise<void> => {
  if (hasHydratedCart || !isAuthenticated()) return;
  hasHydratedCart = true;

  const generation = syncGeneration;
  await enqueueCartOperation(async () => {
    store.dispatch(CartSlice.fetchingRefresh(true));
    try {
      const { items } = store.getState().cart;

      if (options?.skipPush && items.length > 0) return;
      if (isEpochStale(generation)) return;

      const payloadItems = toCartApiItems(items);
      const cartOpId = createCartOpId();
      const response =
        items.length > 0 ? await UserService.syncCart(payloadItems, { cartOpId }) : await UserService.checkUserCart({ cartOpId });

      if (isEpochStale(generation)) return;
      if (!shouldRejectEmptyServerCart(payloadItems.length, response)) {
        applyServerCart(response);
      }
    } catch (_error) {
      // Keep persisted local cart for checkout retry.
    } finally {
      if (!isEpochStale(generation)) {
        store.dispatch(CartSlice.fetchingRefresh(false));
      }
    }
  });
};

const persistCartBeforeLogout = async (): Promise<void> => {
  clearDebounce();
  await flushPendingSync();
};

const handleAuthInvalidated = (): void => {
  clearDebounce();
  bumpEpoch();
  const { items } = store.getState().cart;
  applyCartData(items, null);
  store.dispatch(CartSlice.refreshSyncedBaseline({}));
};

const resetCartSession = (): void => {
  clearDebounce();
  bumpEpoch();
  hasHydratedCart = false;
};

const syncCartForCheckout = async (items: Item[]): Promise<CartResponse | null> => {
  if (!isAuthenticated()) return null;
  const cartOpId = createCartOpId();
  return enqueueCartOperation(() => runSync(items, cartOpId));
};

const attachVisibilityFlush = (): void => {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushPendingSync();
    }
  });
};

attachVisibilityFlush();

const getCartFingerprint = (items: Item[]): string =>
  items
    .map((item) => `${getCartItemKey(item)}:${item.quantity}`)
    .sort()
    .join('|');

const hasPurchasableItems = (items: Item[]): boolean => items.length > 0 && !computeSomeItemOutOfStock(items);

const CartUtils = {
  addToCart,
  increaseOrDecreaseItemInCart,
  removeToCart,
  discardCart,
  clearCartAfterPurchase,
  reconcileCartAfterAuth,
  hydrateCart,
  persistCartBeforeLogout,
  handleAuthInvalidated,
  resetCartSession,
  flushPendingSync,
  syncCartForCheckout,
  getCartFingerprint,
  hasPurchasableItems,
  computeSomeItemOutOfStock,
  toCartApiItems,
  isSameCartItem,
  applyServerCart,
};

export default CartUtils;
